<template>
  <v-app :class="{'new-shell-ui': newUiFlag, 'vh-fix': newVhFix}">
    <NavDrawer
        v-if="logged"
        v-model="drawerOpen"
        @logout="logout"
    />
    <MusicPlayer v-if="logged"/>
    <TopBar
        v-if="logged && user.position.id"
        :position-id="user.position.id"
        @toggle-drawer="drawerOpen = !drawerOpen"
    />
    <v-main>
      <router-view/>
    </v-main>
    <TeamBottomSheet v-if="logged && user.position.id"/>
    <NewsPopup v-if="logged" v-model="showNewsPopup" :entries="newsPopupEntries"/>
  </v-app>
</template>

<script>

import {mapActions, mapGetters} from 'vuex';
import NavDrawer from '@/components/layout/NavDrawer';
import TopBar from '@/components/layout/TopBar';
import TeamBottomSheet from '@/components/journey/next/pieces/TeamBottomSheet';
import MusicPlayer from '@/components/utils/MusicPlayer';
import NewsPopup from '@/components/dashboard/NewsPopup';
import {flags, setFlag, AVAILABLE_SKINS, skinSelectItems, syncFlagsFromBackend} from '@/config/featureFlags';
import {appUpdateState, reloadForUpdate} from '@/config/appUpdate';
import {isMobileMixin} from '@/utils/isMobileMixin';
import mercureClient from '@/utils/mercureClient';
import {API_ENTRYPOINT} from '@/config/entrypoint';
import {getNotificationSoundFile, NOTIFICATION_SOUNDS} from '@/config/notificationSounds';
import {unlockNotificationSounds, playNotificationSound as playCachedNotificationSound} from '@/utils/notificationAudio';

// InfoMessage.type (backend App\Enum\InfoMessageTypeEnum) -> featureFlags.js klic; neznamy/chybejici typ spadne pod "notifyOther" (viz fallback v createToast nize).
const INFO_MESSAGE_TYPE_TO_FLAG = {
  ERR: 'notifyErr',
  OTHER: 'notifyOther',
  EXP: 'notifyExp',
  BADGE: 'notifyBadge',
  POKEMON: 'notifyPokemon',
};

export default {
  name: 'App',
  components: {NavDrawer, TopBar, TeamBottomSheet, MusicPlayer, NewsPopup},
  mixins: [isMobileMixin],
  data() {
    return {
      drawer: null,
      drawerOpen: false,
      logged: false,
      mercureRefreshTimer: null,
      showNewsPopup: false,
      // Zamrazena kopie nevidenych novinek v okamziku otevreni popupu - "unseenNewsEntries" je
      // live getter nad state.seenIds, ktery se meni s kazdym markSeen() behem prolistovani
      // (viz NewsPopup.vue); bez zamrazeni by se pole pod swiperem prubezne zkracovalo.
      newsPopupEntries: [],
    }
  },
  watch: {
    user() {
      this.logged = this.isUserLogged();
    },
    token() {
      this.logged = this.isUserLogged();
    },
    infoMessages() {
      this.loadNews();
    },
    // Mercure spojeni podle prihlaseni; immediate at se navaze i pri prvnim nacteni appky, kdyz uz je uzivatel prihlasen (refresh stranky apod.).
    logged: {
      immediate: true,
      handler(isLogged) {
        if (isLogged) {
          this.connectMercure();
          this.checkUnseenNews();
        } else {
          clearTimeout(this.mercureRefreshTimer);
          mercureClient.disconnect();
        }
      },
    },
    // Jedine misto, ktere $vuetify.theme.dark skutecne prepina, at uz se flag zmeni z debug switche v App.vue nebo v NavDrawer.vue.
    darkThemeFlag: {
      immediate: true,
      handler(v) {
        this.$vuetify.theme.dark = v;
      },
    },
    // Vymeni <link> na aktivni skin CSS pri kazde zmene flagu vc. prvniho nacteni appky (immediate). Viz docs/superpowers/specs/2026-08-04-theme-skin-system-design.md.
    skinFlag: {
      immediate: true,
      handler(v) {
        this.applySkin(v);
      },
    },
    // Drzi $i18n.locale a Vuetify lang.current v souladu s flags.language,
    // at uz se zmeni z Options.vue nebo (budouci) jineho mista. Immediate at
    // se nastavi i pri prvnim nacteni appky. Viz
    // docs/superpowers/specs/2026-08-24-i18n-cz-en-hyb-design.md sekce E/F.
    languageFlag: {
      immediate: true,
      handler(v) {
        this.$i18n.locale = v;
        this.$vuetify.lang.current = v;
      },
    },
    // registerServiceWorker.js preklopi appUpdateState.available, jakmile detekuje novou verzi (viz config/appUpdate.js) - watch misto immediate handleru v created(), kde jeste zadny update nemohl byt znamy.
    updateAvailable(v) {
      if (v) {
        this.showUpdateToast();
      }
    },
  },
  computed: {
    ...mapGetters({
      user: 'user/item',
      token: 'user/token',
      infoMessages: 'trainer/infoMessages',
      buttonVolume: 'journey/buttonVolume',
      unseenNewsEntries: 'changelog/unseenEntries',
    }),
    // Text zije v i18n (dashboard.news.entries.<key>), stejny princip jako News.vue localizedEntries.
    localizedUnseenNews() {
      return this.unseenNewsEntries.map((entry) => ({
        ...entry,
        text: this.$t('dashboard.news.entries.' + entry.key),
      }));
    },
    // "newUi" je v LOCKED_ON (featureFlags.js), vzdy true - jediny prepinac, co ho kdy nastavoval, byl smazan spolu se zbytkem stareho skeletu.
    newUiFlag() {
      return flags.newUi;
    },
    darkThemeFlag: {
      get() {
        return flags.darkTheme;
      },
      set(v) {
        setFlag('darkTheme', v);
      },
    },
    skinFlag: {
      get() {
        return flags.skin;
      },
      set(v) {
        setFlag('skin', v);
      },
    },
    languageFlag: {
      get() {
        return flags.language;
      },
      set(v) {
        setFlag('language', v);
      },
    },
    availableSkins() {
      return skinSelectItems();
    },
    // Zapina se URL parametrem ?newVhFix=1, zadny UI prepinac zatim - viz ".vh-fix" nize a main.js.
    newVhFix() {
      return flags.newVhFix;
    },
    updateAvailable() {
      return appUpdateState.available;
    },
  },
  created() {
    this.logged = this.isUserLogged();

    if (this.token && this.logged && (this.$router.currentRoute.path === '/login' || this.$router.currentRoute.path === '/')) {
      this.$router.push({name: 'Journey'});
    } else if (!this.logged && this.$router.currentRoute.path !== '/login') {
      this.$router.push({name: 'Login'});
    }
    if (this.token) {
      syncFlagsFromBackend();
    }
    this.loadNews();
    mercureClient.onMessage('player_message', this.onPlayerMessageEvent);
  },
  mounted() {
    // Notifikacni zvuky (vc. PvP yeat-bell/boxing-bell) se pousteji asynchronne
    // z Mercure push callbacku, ne primo z uzivatelova kliknuti. Desktop
    // Chrome/Firefox po prvnim gestu odemyka autoplay pro celou stranku, ale
    // mobilni WebKit (iOS Safari, i nainstalovana PWA) vaze odemceni na
    // KONKRETNI <audio> element - proto se pri prvnim gestu odemykaji vsechny
    // sdilene cachovane elementy (viz utils/notificationAudio.js), ktere se
    // pak i na mobilu pri realnem prehrani jen znovu spusti, misto vytvoreni
    // noveho (na mobilu porad zablokovaneho) Audio() objektu.
    const unlockAudio = () => {
      unlockNotificationSounds([
        ...NOTIFICATION_SOUNDS.map((s) => s.file),
        'yeat-bell.mp3',
        'boxing-bell.wav',
      ]);
    };
    ['pointerdown', 'keydown', 'touchstart'].forEach((evt) => {
      document.addEventListener(evt, unlockAudio, {once: true, passive: true});
    });
  },
  methods: {
    ...mapActions({
      // userLogged: 'user/isLogged',
      getUser: 'user/loadItem',
      logoutUser: 'user/logout',
      fetchTrainerObject: 'trainer/fetchObject',
      loadSeenNewsIds: 'changelog/loadSeenIds',
      markNewsSeen: 'changelog/markSeen',
    }),
    logout() {
      // Plny reload, ne router.push - sdilene store moduly (pokemon.items mezi TeamBottomSheet.vue/PvpBattleScreen.vue, journey, trainer, dashboard, city...) se po router.push nereseovaly; opozdeny fetch ze stareho uctu (napr. rozjety pred odhlasenim) mohl dorazit az po prihlaseni novym uctem a prepsat sdilena data cizim tymem. Stejny problem uz reseny v impersonate()/returnToAdmin().
      this.logoutUser().then(
          () => {
            window.location.href = '/login';
          }
      )
    },
    isUserLogged() {
      return this.user && this.token !== "";
    },
    sleep(ms) {
      return new Promise(resolve => setTimeout(resolve, ms));
    },
    loadNews(i = 0) {
      for (i; i < this.infoMessages.length; i++) {
        if (this.infoMessages[i]['new']) {
          this.createToast(i);
          i++;
          this.sleep(1000).then(() => {
            this.loadNews(i++);
          });
          break;
        }
      }
    },
    createToast(i) {
      const message = this.infoMessages[i];
      const flagKey = INFO_MESSAGE_TYPE_TO_FLAG[message.type] || 'notifyOther';
      if (flags[flagKey] === false) {
        return;
      }
      this.$toast.info(message.text, {
        position: "bottom-left",
        timeout: 4032,
        closeOnClick: true,
        pauseOnFocusLoss: true,
        pauseOnHover: true,
        draggable: true,
        draggablePercent: 0.6,
        showCloseButtonOnHover: false,
        hideProgressBar: true,
        closeButton: "button",
        icon: "v-icon notranslate ml-0 pl-0 mdi mdi-pokeball theme--dark",
        rtl: false
      });
    },
    // timeout:false (nezmizi samo) + closeOnClick:false s explicitnim onClick - klik na telo reloadne, klik na X jen zavre banner beze ztraty rozehraneho souboje (closeButton handler ve vue-toastification onClick nevola).
    showUpdateToast() {
      this.$toast.info(this.$t('common.updateAvailable'), {
        position: 'top-center',
        timeout: false,
        closeOnClick: false,
        pauseOnFocusLoss: false,
        draggable: false,
        showCloseButtonOnHover: false,
        hideProgressBar: true,
        closeButton: 'button',
        icon: 'v-icon notranslate ml-0 pl-0 mdi mdi-refresh theme--dark',
        onClick: () => {
          reloadForUpdate();
        },
      });
    },
    // Vytvori (pri prvnim volani) nebo aktualizuje <link id="skin-stylesheet"> tak, aby ukazoval na CSS soubor aktivniho skinu. public/skins/*.css jsou staticke soubory, stahnou se hned pri registraci service workeru spolu se zbytkem appky (viz vue.config.js pwa precache).
    applySkin(skinName) {
      // Fallback na 'normal' pro neplatne jmeno - bez tohohle by preklep/zastarala localStorage hodnota tise rozbila cely vzhled bez cesty zpet pres UI.
      const skin = AVAILABLE_SKINS.includes(skinName) ? skinName : 'normal';
      let link = document.getElementById('skin-stylesheet');
      if (!link) {
        link = document.createElement('link');
        link.id = 'skin-stylesheet';
        link.rel = 'stylesheet';
        document.head.appendChild(link);
      }
      link.href = `${process.env.BASE_URL}skins/${skin}.css`;
    },
    // Ziska cerstvy subscriber JWT/topic z backendu (/api/mercure-token) a navaze EventSource spojeni. Viz docs/superpowers/specs/2026-08-17-mercure-notifications-design.md.
    connectMercure() {
      const url = new URL('api/mercure-token', API_ENTRYPOINT);
      this.fetchTrainerObject({url, method: 'GET'}).then((data) => {
        if (data && data.topic) {
          mercureClient.connect(data.topic);
          // JWT plati 1h - obnovit o 10 min driv, at spojeni nikdy neprestane fungovat kvuli expiraci uprostred hrani. Preventivni timer misto reakce na EventSource "error" - ten fire-uje i pri beznych sitovych vypadcich, ktere native reconnect resi sam.
          clearTimeout(this.mercureRefreshTimer);
          this.mercureRefreshTimer = setTimeout(this.connectMercure, 50 * 60 * 1000);
        } else {
          // fetchTrainerObject interne polyka chyby (resolvuje na undefined misto rejectu) - bez explicitniho retry by dalsi timer nikdy nevznikl a notifikace by tise natrvalo prestaly fungovat.
          console.warn('Mercure connect failed (token fetch nevratil data), zkusim znovu za 2 min');
          clearTimeout(this.mercureRefreshTimer);
          this.mercureRefreshTimer = setTimeout(this.connectMercure, 2 * 60 * 1000);
        }
      });
    },
    // Nacte "videno/nevideno" stav z backendu a otevre popup, pokud existuji nevidene novinky.
    // Volano z immediate "logged" watcheru - pokryva jak prvni nacteni appky s ulozenym tokenem,
    // tak cerstve prihlaseni (login/impersonate meni user/token, ktere "logged" prepinaji).
    // Vsechny zobrazene novinky se oznaci jako videne rovnou tady, pri otevreni - ne az
    // postupne podle toho, kam hrac ve swiperu doscrolluje (NewsPopup.vue). Kdo popup jen
    // zavre bez prolistovani, uz se s tymiz polozkami priste znovu obtezovat nebude.
    checkUnseenNews() {
      this.loadSeenNewsIds().then(() => {
        if (this.unseenNewsEntries.length > 0) {
          this.newsPopupEntries = this.localizedUnseenNews;
          this.markNewsSeen(this.newsPopupEntries.map((entry) => entry.key));
          this.showNewsPopup = true;
        }
      });
    },
    onPlayerMessageEvent(payload) {
      if (payload.sender && this.user && payload.sender.id !== this.user.id && this.user.notificationsEnabled !== false) {
        this.playNotificationSound();
      }
    },
    playNotificationSound() {
      // Notifikace nemaji vlastni slider hlasitosti (jen on/off), takze
      // NEjedou pres buttonVolume ("Sounds" - efekty/kliky, samostatna vec) -
      // hraji vzdy na plnou.
      const file = getNotificationSoundFile(this.user && this.user.notificationSound);
      playCachedNotificationSound(file, 1);
    },
  },
}
</script>

<style>
/* v-main dostava pevnou height:100vh (misto rustu podle obsahu) - jinak TopBaruv padding-top pridal navic vysku a dole vznikl prazdny scrollovatelny pruh. v-main__wrap dedi zmensenou vysku a scrolluje sam (overflow-y:auto), at funguje i na strankach bez vlastniho ".xxx-page__scroll" wrapperu. Viz claude/components/layout-shared-utils.md pro historii pokusu (vc. zamitnuteho 100svh). */
.new-shell-ui.v-application {
  height: 100vh;
  overflow: hidden;
}
.new-shell-ui .v-main {
  height: 100vh;
  overflow: hidden;
}
.new-shell-ui .v-main__wrap {
  height: 100%;
  overflow-y: auto;
}

/* newVhFix (?newVhFix=1, viz featureFlags.js/main.js) pouzije vysku skutecne zmerenou v JS (window.visualViewport -> "--app-vh") misto CSS vh/svh jednotky; fallback na 100vh, kdyby JS hodnotu nestihl nastavit. */
.new-shell-ui.vh-fix.v-application {
  height: var(--app-vh, 100vh);
}
/* Vuetify vlastni .v-application--wrap ma natvrdo flex-grow:1 - bez explicitniho min-height tady by .v-main nize prebilo zpet na plnou vysku teto mezivrstvy. */
.new-shell-ui.vh-fix .v-application--wrap {
  min-height: var(--app-vh, 100vh);
}
.new-shell-ui.vh-fix .v-main {
  height: var(--app-vh, 100vh);
}
/* .v-navigation-drawer je position:fixed (neni potomkem vysky .v-main vyse) a Vuetify mu navic nastavuje vysku jako inline style - proto nutne !important, jako jediny pripad v tomto souboru. */
.new-shell-ui.vh-fix .v-navigation-drawer {
  height: var(--app-vh, 100vh) !important;
}

/* Docasny override, jen mobil: stranky maji fixni pl-10/ml-10 kompenzujici 200px desktopovy sidebar, na mobilu (bez sidebaru) by zbytecne zabiraly misto. Fyzicke smazani trid az v cleanup fazi. */
@media (max-width: 959px) {
  .new-shell-ui .pl-10 {
    padding-left: 8px !important;
  }
  .new-shell-ui .ml-10 {
    margin-left: 8px !important;
  }
  /* road progress se na mobilu presunul do horniho baru (TopBar) - schovat original ve Wild.vue */
  .new-shell-ui .road-progress-banner {
    display: none !important;
  }
}
</style>
